import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiRequest, getImageUrl } from '../../utils/api';
import { ICONS, ICON_CATEGORIES } from '../../utils/icons';
import { ColorEditor, COLOR_SECTIONS } from '../../components/admin/ColorEditor';
import MediaManager from '../../components/admin/MediaManager';
import FontSelect from '../../components/admin/FontSelect';

const SettingsPage = () => {
    const { organizationId } = useParams();
    const [activeTab, setActiveTab] = useState('general');
    const [settings, setSettings] = useState({});
    const [organizationInfo, setOrganizationInfo] = useState(null);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [toast, setToast] = useState(null);

    // Navbar items state
    const [navbarItems, setNavbarItems] = useState([]);

    // Toast helper
    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const tabs = [
        { id: 'general', label: 'Genel', icon: '🏪' },
        { id: 'media', label: 'Medya', icon: '🖼️' },
        { id: 'design', label: 'Tasarım', icon: '🎨' },
        { id: 'features', label: 'Özellikler', icon: '⚡' },
        { id: 'storage', label: 'Depolama', icon: '💾' },
        { id: 'analytics', label: 'Analitik', icon: '📊' },
        { id: 'custom', label: 'Özel Kod', icon: '💻' }
    ];

    useEffect(() => {
        loadSettings();
    }, [organizationId]);

    const loadSettings = async () => {
        setLoading(true);
        try {
            const [settingsRes, orgRes, categoriesRes, productsRes] = await Promise.all([
                apiRequest(`/organizations/${organizationId}/settings`),
                apiRequest(`/organizations/${organizationId}/settings/organization`),
                apiRequest(`/organizations/${organizationId}/categories`).catch(() => ({ data: [] })),
                apiRequest(`/organizations/${organizationId}/products?limit=50`).catch(() => ({ data: [] }))
            ]);

            const settingsData = settingsRes.data || {};
            const orgInfo = orgRes.data || null;
            setOrganizationInfo(orgInfo);
            setCategories(categoriesRes.data || []);
            setProducts(productsRes.data || []);

            // If restaurant_name setting is empty or missing, use restaurant name from organization info
            if (!settingsData.restaurant_name && orgInfo?.restaurant_name) {
                settingsData.restaurant_name = orgInfo.restaurant_name;
            } else if (!settingsData.restaurant_name && orgInfo?.name) {
                settingsData.restaurant_name = orgInfo.name;
            }
            
            // If restaurant_slug setting is empty or missing, use restaurant slug from organization info
            if (!settingsData.restaurant_slug) {
                const restaurantSlug = orgInfo?.restaurant_slug || orgInfo?.organization_slug || '';
                if (restaurantSlug) {
                    settingsData.restaurant_slug = restaurantSlug;
                }
            }

            setSettings(settingsData);

            // Parse navbar items or set defaults
            if (settingsData.navbar_items) {
                try {
                    const parsed = JSON.parse(settingsData.navbar_items);
                    setNavbarItems(parsed);
                } catch (e) {
                    setNavbarItems(getDefaultNavbarItems());
                }
            } else {
                setNavbarItems(getDefaultNavbarItems());
            }
        } catch (error) {
            console.error('Load settings error:', error);
            setError(error.message || 'Ayarlar yüklenirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    // Default navbar items
    const getDefaultNavbarItems = () => [
        { id: 'home', label: 'Ana Sayfa', icon: 'home', type: 'internal', target: 'home', enabled: true },
        { id: 'categories', label: 'Kategoriler', icon: 'grid', type: 'internal', target: 'categories', enabled: true },
        { id: 'campaigns', label: 'Kampanyalar', icon: 'gift', type: 'internal', target: 'campaigns', enabled: true },
        { id: 'cocktails', label: 'İmza Kokteyller', icon: 'cocktail', type: 'category', target: '', enabled: true },
        { id: 'contact', label: 'Bize Yazın', icon: 'message', type: 'internal', target: 'contact', enabled: true }
    ];

    const handleSettingChange = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const handleNavbarItemChange = (index, field, value) => {
        // Handle special operations (add, remove, reorder)
        if (index === -1) {
            if (field === 'add' || field === 'remove' || field === 'reorder') {
                setNavbarItems(value);
                handleSettingChange('navbar_items', JSON.stringify(value));
                return;
            }
        }
        
        // Normal field update
        const newItems = [...navbarItems];
        newItems[index] = { ...newItems[index], [field]: value };
        setNavbarItems(newItems);
        handleSettingChange('navbar_items', JSON.stringify(newItems));
    };

    const handleSave = async (tabId = null) => {
        setSaving(true);
        setError(null);
        setSuccess(false);

        try {
            const tab = tabId || activeTab;

            // Save restaurant info (from General tab)
            if (tab === 'general') {
                const restaurantData = {
                    name: getSettingValue('restaurant_name') || organizationInfo?.name || '',
                    slug: getSettingValue('restaurant_slug') || organizationInfo?.restaurant_slug || organizationInfo?.organization_slug || '',
                    description: getSettingValue('restaurant_description') || organizationInfo?.description || '',
                    phone: getSettingValue('restaurant_phone') || organizationInfo?.phone || '',
                    email: getSettingValue('restaurant_email') || organizationInfo?.email || '',
                    address: getSettingValue('restaurant_address') || organizationInfo?.address || '',
                    whatsapp_number: getSettingValue('whatsapp_number') || organizationInfo?.whatsapp_number || '',
                    instagram_url: getSettingValue('instagram_url') || organizationInfo?.instagram_url || '',
                    facebook_url: getSettingValue('facebook_url') || organizationInfo?.facebook_url || ''
                };

                // Update restaurant info
                await apiRequest(`/organizations/${organizationId}/settings/organization`, {
                    method: 'PUT',
                    body: JSON.stringify(restaurantData),
                    headers: { 'Content-Type': 'application/json' }
                });
            }

            // Save settings (design, features, analytics, custom code)
            const settingsToSave = { ...settings };
            // Remove restaurant info from settings (it's in restaurants table now)
            if (tab === 'general') {
                delete settingsToSave.restaurant_name;
                delete settingsToSave.restaurant_slug;
                delete settingsToSave.restaurant_description;
                delete settingsToSave.restaurant_phone;
                delete settingsToSave.restaurant_email;
                delete settingsToSave.restaurant_address;
                delete settingsToSave.restaurant_logo;
                delete settingsToSave.whatsapp_number;
                delete settingsToSave.instagram_url;
                delete settingsToSave.facebook_url;
            }

            // Filter settings by tab
            if (tab === 'design') {
                // Include all color settings + font + navbar
                const designKeys = [
                    // General Colors
                    'primary_color', 'secondary_color', 'accent_color',
                    // Background Colors
                    'background_color', 'card_bg_color', 'modal_bg_color',
                    // Text Colors
                    'text_color', 'text_secondary_color', 'text_muted_color',
                    'home_categories_title_color', 'home_categories_subtitle_color',
                    // Header Colors
                    'header_bg_color', 'header_text_color', 'header_border_color',
                    // Navbar Colors
                    'navbar_bg_color', 'navbar_text_color', 'navbar_active_color', 'navbar_border_color',
                    // Sidebar Colors
                    'sidebar_bg_color', 'sidebar_header_start', 'sidebar_header_end', 'sidebar_logo_bg',
                    'sidebar_text_color', 'sidebar_hover_color', 'sidebar_active_color', 'sidebar_active_text_color',
                    // Category Colors
                    'category_bg_color', 'category_text_color', 'category_border_color', 'category_hover_color',
                    'category_banner_start_color', 'category_banner_end_color',
                    // Product Colors
                    'product_bg_color', 'product_title_color', 'product_price_color', 
                    'product_description_color', 'product_border_color',
                    // Discount Colors
                    'discount_badge_bg_color', 'discount_badge_text_color', 'discount_price_color',
                    'discount_old_price_color', 'campaign_bg_color', 'campaign_text_color',
                    // Button Colors
                    'button_primary_color', 'button_primary_text_color', 
                    'button_secondary_color', 'button_secondary_text_color',
                    // Like Colors
                    'like_color', 'dislike_color', 'like_bg_color', 'dislike_bg_color',
                    // Font & Navbar Items
                    'font_family', 'general_font', 'category_title_font', 'category_description_font', 'product_title_font', 'product_description_font', 'navbar_items'
                ];
                Object.keys(settingsToSave).forEach(key => {
                    if (!designKeys.includes(key)) {
                        delete settingsToSave[key];
                    }
                });
            } else if (tab === 'features') {
                const featureKeys = ['show_prices', 'show_likes', 'show_descriptions', 'show_allergens', 'show_preparation_time', 'show_calories', 'enable_search', 'enable_whatsapp', 'enable_contact_form', 'enable_voting'];
                Object.keys(settingsToSave).forEach(key => {
                    if (!featureKeys.includes(key)) {
                        delete settingsToSave[key];
                    }
                });
            } else if (tab === 'analytics') {
                const analyticsKeys = ['google_analytics_id', 'google_analytics_code', 'hotjar_id', 'hotjar_code'];
                Object.keys(settingsToSave).forEach(key => {
                    if (!analyticsKeys.includes(key)) {
                        delete settingsToSave[key];
                    }
                });
            } else if (tab === 'custom') {
                const customKeys = ['custom_head_code', 'custom_body_start_code', 'custom_body_end_code', 'custom_css'];
                Object.keys(settingsToSave).forEach(key => {
                    if (!customKeys.includes(key)) {
                        delete settingsToSave[key];
                    }
                });
            }

            if (Object.keys(settingsToSave).length > 0 || tab === 'general') {
                if (Object.keys(settingsToSave).length > 0) {
            await apiRequest(`/organizations/${organizationId}/settings/bulk`, {
                method: 'PUT',
                        body: JSON.stringify(settingsToSave),
                        headers: { 'Content-Type': 'application/json' }
                    });
                }

                // Reload data
                await loadSettings();

            setSuccess(true);
                showToast('Ayarlar başarıyla kaydedildi!', 'success');
            setTimeout(() => setSuccess(false), 3000);
            }
        } catch (err) {
            setError(err.message || 'Ayarlar kaydedilirken bir hata oluştu');
            showToast('Ayarlar kaydedilirken bir hata oluştu', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Auto-save for features tab
    const handleFeatureToggle = async (key, value) => {
        handleSettingChange(key, value);
        
        // Auto-save feature toggle
        try {
            await apiRequest(`/organizations/${organizationId}/settings/bulk`, {
                method: 'PUT',
                body: JSON.stringify({ [key]: value }),
                headers: { 'Content-Type': 'application/json' }
            });
            
            const featureLabels = {
                'show_prices': 'Fiyatları Göster',
                'show_likes': 'Beğeni Sayısını Göster',
                'show_descriptions': 'Açıklamaları Göster',
                'show_allergens': 'Alerjen Bilgilerini Göster',
                'show_preparation_time': 'Hazırlık Süresini Göster',
                'show_calories': 'Kalori Bilgisini Göster',
                'enable_search': 'Arama Özelliği',
                'enable_whatsapp': 'WhatsApp Butonu',
                'enable_contact_form': 'İletişim Formu',
                'enable_voting': 'Oylama Sistemi'
            };
            
            const isEnabled = value === 'true' || value === true;
            showToast(`${featureLabels[key] || key} ${isEnabled ? 'açıldı' : 'kapatıldı'}`, 'success');
        } catch (err) {
            showToast('Ayar kaydedilirken bir hata oluştu', 'error');
        }
    };

    const handleLogoUpload = async (file) => {
        try {
            const formData = new FormData();
            formData.append('logo', file);

            const response = await fetch(`/api/organizations/${organizationId}/settings/organization/logo`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formData
            });

            const result = await response.json();

            if (result.success) {
                // Update only logo in organizationInfo, don't reload all settings
                setOrganizationInfo(prev => ({
                    ...prev,
                    logo: result.data.logo
                }));
                // Also update in settings
                setSettings(prev => ({
                    ...prev,
                    restaurant_logo: result.data.logo
                }));
                showToast('Logo başarıyla yüklendi!', 'success');
            } else {
                showToast(result.message || 'Logo yüklenirken bir hata oluştu', 'error');
            }
        } catch (err) {
            showToast('Logo yüklenirken bir hata oluştu', 'error');
        }
    };

    const getSettingValue = (key, defaultValue = '') => {
        let value = settings[key] !== undefined ? settings[key] : defaultValue;
        
        // Fallback to organization info for restaurant_name
        if (key === 'restaurant_name' && !value && organizationInfo) {
            value = organizationInfo.restaurant_name || organizationInfo.name || defaultValue;
        }
        
        return value;
    };

    const getBooleanValue = (key, defaultValue = false) => {
        const val = settings[key];
        if (val === 'true' || val === true) return true;
        if (val === 'false' || val === false) return false;
        return defaultValue;
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                    <h1 className="text-3xl font-bold text-gray-900">Ayarlar</h1>
                        <p className="text-gray-600 mt-1">Restoranınızın tüm ayarlarını buradan yönetin</p>
                    </div>
                </div>

                {/* Alerts */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-3">
                        <span className="text-xl">❌</span>
                        {error}
                    </div>
                )}

                {success && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 flex items-center gap-3">
                        <span className="text-xl">✅</span>
                        Ayarlar başarıyla kaydedildi!
                    </div>
                )}

                {/* Toast Notification */}
                {toast && (
                    <div className={`fixed top-4 right-4 z-50 p-4 rounded-xl shadow-lg flex items-center gap-3 animate-slide-in ${
                        toast.type === 'success' 
                            ? 'bg-green-50 border border-green-200 text-green-700' 
                            : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                        <span className="text-xl">{toast.type === 'success' ? '✅' : '❌'}</span>
                        <span className="font-medium">{toast.message}</span>
                    </div>
                )}

                <style>{`
                    @keyframes slide-in {
                        from {
                            transform: translateX(100%);
                            opacity: 0;
                        }
                        to {
                            transform: translateX(0);
                            opacity: 1;
                        }
                    }
                    .animate-slide-in {
                        animation: slide-in 0.3s ease-out;
                    }
                `}</style>

                {/* Tabs */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div className="border-b border-gray-200">
                        <nav className="flex overflow-x-auto">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                                        activeTab === tab.id
                                            ? 'border-orange-500 text-orange-600 bg-orange-50'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <span className="text-lg">{tab.icon}</span>
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>

                    <div className="p-6">
                        {/* Tab-specific Save Button */}
                        {activeTab !== 'features' && (
                            <div className="mb-6 flex justify-end">
                                <button
                                    onClick={() => handleSave(activeTab)}
                                    disabled={saving}
                                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-semibold hover:from-orange-600 hover:to-red-600 disabled:opacity-50 transition shadow-lg"
                                >
                                    {saving ? (
                                        <span className="flex items-center gap-2">
                                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                            </svg>
                                            Kaydediliyor...
                                        </span>
                                    ) : 'Değişiklikleri Kaydet'}
                                </button>
                            </div>
                        )}
                        {/* General Tab */}
                        {activeTab === 'general' && (
                            <GeneralSettings 
                                settings={settings}
                                organizationInfo={organizationInfo}
                                onChange={handleSettingChange}
                                getSettingValue={getSettingValue}
                            />
                        )}

                        {/* Media Tab */}
                        {activeTab === 'media' && (
                            <MediaSettings 
                                settings={settings}
                                organizationInfo={organizationInfo}
                                onChange={handleSettingChange}
                                getSettingValue={getSettingValue}
                                onLogoUpload={handleLogoUpload}
                                organizationId={organizationId}
                                showToast={showToast}
                            />
                        )}

                        {/* Design Tab */}
                        {activeTab === 'design' && (
                            <DesignSettings 
                                settings={settings}
                                navbarItems={navbarItems}
                                onChange={handleSettingChange}
                                onNavbarChange={handleNavbarItemChange}
                                getSettingValue={getSettingValue}
                                categories={categories}
                                products={products}
                                organizationId={organizationId}
                                restaurant={organizationInfo}
                                onSave={() => handleSave('design')}
                                saving={saving}
                            />
                        )}

                        {/* Features Tab */}
                        {activeTab === 'features' && (
                            <FeaturesSettings 
                                settings={settings}
                                onChange={handleFeatureToggle}
                                getBooleanValue={getBooleanValue}
                            />
                        )}

                        {/* Storage Tab */}
                        {activeTab === 'storage' && (
                            <MediaManager organizationId={organizationId} />
                        )}

                        {/* Analytics Tab */}
                        {activeTab === 'analytics' && (
                            <AnalyticsSettings 
                                settings={settings}
                                onChange={handleSettingChange}
                                getSettingValue={getSettingValue}
                            />
                        )}

                        {/* Custom Code Tab */}
                        {activeTab === 'custom' && (
                            <CustomCodeSettings 
                                settings={settings}
                                onChange={handleSettingChange}
                                getSettingValue={getSettingValue}
                            />
                        )}
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

// General Settings Component
const GeneralSettings = ({ settings, organizationInfo, onChange, getSettingValue }) => {
    return (
        <div className="space-y-8">
            {/* Restaurant Info */}
                            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600">🏪</span>
                    Restoran Bilgileri
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Restoran Adı
                                </label>
                                <input
                                    type="text"
                            value={getSettingValue('restaurant_name') || organizationInfo?.name || ''}
                            onChange={(e) => onChange('restaurant_name', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="Örn: Nox House"
                                />
                            </div>
                            <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Menü Linki
                        </label>
                        <div className="flex items-center">
                            <span className="px-4 py-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-xl text-gray-500 text-sm">
                                menu.zehuma.com/
                            </span>
                            <input
                                type="text"
                                value={getSettingValue('restaurant_slug') || organizationInfo?.restaurant_slug || organizationInfo?.organization_slug || ''}
                                readOnly
                                disabled
                                className="flex-1 px-4 py-3 bg-gray-50 border border-gray-300 rounded-r-xl text-gray-600 cursor-not-allowed"
                                placeholder="sultanlar-tost"
                            />
                            <button
                                onClick={() => {
                                    const slug = getSettingValue('restaurant_slug') || organizationInfo?.restaurant_slug || organizationInfo?.organization_slug || '';
                                    const menuLink = `https://menu.zehuma.com/${slug}`;
                                    navigator.clipboard.writeText(menuLink).then(() => {
                                        showToast('Menü linki kopyalandı!', 'success');
                                    }).catch(() => {
                                        showToast('Kopyalama başarısız', 'error');
                                    });
                                }}
                                className="ml-2 px-4 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition flex items-center gap-2"
                                title="Menü linkini kopyala"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Menü URL'si (değiştirilemez)</p>
                    </div>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Restoran Açıklaması
                                </label>
                                <textarea
                        value={getSettingValue('restaurant_description') || organizationInfo?.description || ''}
                        onChange={(e) => onChange('restaurant_description', e.target.value)}
                                    rows="3"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                        placeholder="Restoranınızı kısaca tanımlayın..."
                                />
                            </div>

            {/* Contact Info */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">📞</span>
                    İletişim Bilgileri
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Telefon
                        </label>
                        <input
                            type="tel"
                            value={getSettingValue('restaurant_phone') || organizationInfo?.phone || ''}
                            onChange={(e) => onChange('restaurant_phone', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="+90 5XX XXX XX XX"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            E-posta
                        </label>
                        <input
                            type="email"
                            value={getSettingValue('restaurant_email') || organizationInfo?.email || ''}
                            onChange={(e) => onChange('restaurant_email', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="info@example.com"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            WhatsApp Numarası
                        </label>
                        <input
                            type="tel"
                            value={getSettingValue('whatsapp_number') || organizationInfo?.whatsapp_number || ''}
                            onChange={(e) => onChange('whatsapp_number', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="+905XXXXXXXXX"
                        />
                        <p className="text-xs text-gray-500 mt-1">Ülke kodu ile birlikte yazın</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Adres
                        </label>
                        <input
                            type="text"
                            value={getSettingValue('restaurant_address') || organizationInfo?.address || ''}
                            onChange={(e) => onChange('restaurant_address', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="Mahalle, Cadde, No, İlçe/İl"
                        />
                    </div>
                        </div>
                    </div>

            {/* Social Media */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600">🌐</span>
                    Sosyal Medya
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Instagram URL
                        </label>
                        <input
                            type="url"
                            value={getSettingValue('instagram_url') || organizationInfo?.instagram_url || ''}
                            onChange={(e) => onChange('instagram_url', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="https://instagram.com/username"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Facebook URL
                        </label>
                        <input
                            type="url"
                            value={getSettingValue('facebook_url') || organizationInfo?.facebook_url || ''}
                            onChange={(e) => onChange('facebook_url', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="https://facebook.com/pagename"
                        />
                    </div>
                </div>
            </div>

        </div>
    );
};

// Icon Picker Component with search
const IconPicker = ({ value, onChange, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    
    const filteredIcons = Object.entries(ICONS).filter(([key, icon]) => {
        const matchesSearch = !search || 
            key.toLowerCase().includes(search.toLowerCase()) ||
            icon.name.toLowerCase().includes(search.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || icon.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });
    
    const currentIcon = ICONS[value];
    
    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm flex items-center gap-2 hover:border-orange-500 transition"
            >
                {currentIcon ? (
                    <>
                        <svg className="w-5 h-5 text-gray-600" fill={currentIcon.fill ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={currentIcon.path} />
                        </svg>
                        <span className="flex-1 text-left">{currentIcon.name}</span>
                    </>
                ) : (
                    <span className="text-gray-400">İkon Seç</span>
                )}
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            
            {isOpen && (
                <div className="absolute z-50 mt-1 w-80 bg-white rounded-xl shadow-xl border border-gray-200 max-h-96 overflow-hidden">
                    {/* Search */}
                    <div className="p-3 border-b">
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="İkon ara..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            autoFocus
                        />
                    </div>
                    
                    {/* Category Tabs */}
                    <div className="flex gap-1 p-2 border-b overflow-x-auto">
                        <button
                            onClick={() => setSelectedCategory('all')}
                            className={`px-2 py-1 text-xs rounded whitespace-nowrap ${selectedCategory === 'all' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        >
                            Tümü
                        </button>
                        {Object.entries(ICON_CATEGORIES).map(([key, name]) => (
                            <button
                                key={key}
                                onClick={() => setSelectedCategory(key)}
                                className={`px-2 py-1 text-xs rounded whitespace-nowrap ${selectedCategory === key ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            >
                                {name}
                            </button>
                        ))}
                    </div>
                    
                    {/* Icons Grid */}
                    <div className="p-2 overflow-y-auto max-h-60 grid grid-cols-5 gap-1">
                        {filteredIcons.map(([key, icon]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => {
                                    onChange(key);
                                    setIsOpen(false);
                                    setSearch('');
                                }}
                                className={`p-2 rounded-lg flex flex-col items-center justify-center hover:bg-orange-50 transition ${value === key ? 'bg-orange-100 ring-2 ring-orange-500' : ''}`}
                                title={icon.name}
                            >
                                <svg className="w-6 h-6 text-gray-700" fill={icon.fill ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={icon.path} />
                                </svg>
                            </button>
                        ))}
                    </div>
                    
                    {filteredIcons.length === 0 && (
                        <div className="p-4 text-center text-gray-500 text-sm">
                            İkon bulunamadı
                        </div>
                    )}
                </div>
            )}
            
            {/* Click outside to close */}
            {isOpen && (
                <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => {
                        setIsOpen(false);
                        setSearch('');
                    }}
                />
            )}
        </div>
    );
};

// Design Settings Component - Full Featured
const DesignSettings = ({ settings, navbarItems, onChange, onNavbarChange, getSettingValue, categories = [], products = [], organizationId, restaurant, onSave, saving }) => {
    const [showAddItem, setShowAddItem] = useState(false);
    const [newItem, setNewItem] = useState({ label: '', icon: 'home', type: 'internal', target: '', url: '' });
    const [fontOptions, setFontOptions] = useState([]);
    const [fontLoading, setFontLoading] = useState(true);

    // Backend proxy üzerinden Google Fonts (CORS bypass - tüm fontlar)
    useEffect(() => {
        let cancelled = false;
        const loadFonts = async () => {
            try {
                const res = await apiRequest('/fonts');
                const fonts = res?.fonts || [];
                if (!cancelled && fonts.length > 0) {
                    setFontOptions(fonts);
                } else if (!cancelled && fonts.length === 0) {
                    setFontOptions([
                        { name: 'Inter', category: 'Sans Serif' },
                        { name: 'Poppins', category: 'Sans Serif' },
                        { name: 'Roboto', category: 'Sans Serif' },
                        { name: 'Open Sans', category: 'Sans Serif' },
                        { name: 'Montserrat', category: 'Sans Serif' },
                        { name: 'Lato', category: 'Sans Serif' },
                        { name: 'Nunito', category: 'Sans Serif' },
                        { name: 'Raleway', category: 'Sans Serif' },
                        { name: 'Playfair Display', category: 'Serif' },
                        { name: 'Oswald', category: 'Sans Serif' }
                    ]);
                }
            } catch (err) {
                console.error('Font listesi yüklenemedi:', err);
                if (!cancelled) {
                    setFontOptions([
                        { name: 'Inter', category: 'Sans Serif' },
                        { name: 'Poppins', category: 'Sans Serif' },
                        { name: 'Roboto', category: 'Sans Serif' },
                        { name: 'Open Sans', category: 'Sans Serif' },
                        { name: 'Montserrat', category: 'Sans Serif' },
                        { name: 'Lato', category: 'Sans Serif' },
                        { name: 'Nunito', category: 'Sans Serif' },
                        { name: 'Raleway', category: 'Sans Serif' },
                        { name: 'Playfair Display', category: 'Serif' },
                        { name: 'Oswald', category: 'Sans Serif' }
                    ]);
                }
            } finally {
                if (!cancelled) setFontLoading(false);
            }
        };
        loadFonts();
        return () => { cancelled = true; };
    }, []);

    // Link target types
    const linkTypes = [
        { value: 'internal', label: 'Menü İçi', description: 'Sayfada bir bölüme git' },
        { value: 'category', label: 'Kategori', description: 'Bir kategoriye git' },
        { value: 'whatsapp', label: 'WhatsApp', description: 'WhatsApp açılır' },
        { value: 'phone', label: 'Telefon', description: 'Arama yapar' },
        { value: 'external', label: 'Dış Link', description: 'Harici bir siteye git' }
    ];

    // Internal targets
    const internalTargets = [
        { value: 'home', label: 'Ana Sayfa' },
        { value: 'categories', label: 'Kategoriler Bölümü' },
        { value: 'campaigns', label: 'Kampanyalar' },
        { value: 'contact', label: 'İletişim Formu' },
        { value: 'footer', label: 'Footer (Alt Kısım)' }
    ];

    const addNavbarItem = () => {
        if (!newItem.label) return;
        
        const item = {
            id: `custom_${Date.now()}`,
            label: newItem.label,
            icon: newItem.icon,
            type: newItem.type,
            target: newItem.type === 'category' ? newItem.target : (newItem.type === 'internal' ? newItem.target : ''),
            url: newItem.type === 'external' || newItem.type === 'whatsapp' || newItem.type === 'phone' ? newItem.url : '',
            enabled: true
        };
        
        const updated = [...navbarItems, item];
        onNavbarChange(-1, 'add', updated);
        setNewItem({ label: '', icon: 'home', type: 'internal', target: '', url: '' });
        setShowAddItem(false);
    };

    const removeNavbarItem = (index) => {
        const updated = navbarItems.filter((_, i) => i !== index);
        onNavbarChange(-1, 'remove', updated);
    };

    const moveNavbarItem = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= navbarItems.length) return;
        
        const updated = [...navbarItems];
        [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
        onNavbarChange(-1, 'reorder', updated);
    };

    return (
        <div className="space-y-8">
            {/* Color Editor with Live Preview */}
            <ColorEditor
                settings={settings}
                onChange={onChange}
                getSettingValue={getSettingValue}
                restaurant={restaurant}
                categories={categories}
                products={products}
                onSave={onSave}
                saving={saving}
            />

            {/* Font - Google Fonts (renk seçiminin altında) */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600">🔤</span>
                    Yazı Tipleri (Google Fonts)
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                    Menüdeki farklı metin alanları için yazı tipi seçin. Tüm fontlar Google Fonts ile senkron. Her seçicide arama yapabilirsiniz.
                    {!fontLoading && fontOptions.length > 0 && (
                        <span className="ml-1 text-indigo-600 font-medium">({fontOptions.length} font)</span>
                    )}
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        {[
                            { key: 'general_font', label: 'Genel Font', desc: 'Kategori/ürün dışındaki tüm yazılar (header, footer, butonlar vb.)' },
                            { key: 'category_title_font', label: 'Kategori Başlık Fontu', desc: 'Kategori adları' },
                            { key: 'category_description_font', label: 'Kategori Açıklama Fontu', desc: 'Kategori açıklamaları' },
                            { key: 'product_title_font', label: 'Ürün Başlık Fontu', desc: 'Ürün adları' },
                            { key: 'product_description_font', label: 'Ürün Açıklama Fontu', desc: 'Ürün açıklamaları' }
                        ].map(({ key, label, desc }) => (
                            <div key={key}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                                <p className="text-xs text-gray-500 mb-2">{desc}</p>
                                <FontSelect
                                    value={getSettingValue(key, getSettingValue('font_family', 'Inter'))}
                                    onChange={(val) => onChange(key, val)}
                                    options={fontOptions}
                                    disabled={fontLoading}
                                    placeholder={fontLoading ? 'Fontlar yükleniyor...' : 'Font seçin'}
                                />
                            </div>
                        ))}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Önizleme</label>
                        <div className="p-6 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 space-y-4">
                            <div style={{ fontFamily: `"${getSettingValue('general_font', getSettingValue('font_family', 'Inter'))}", sans-serif` }}>
                                <p className="text-xs text-gray-500 mb-1">Genel</p>
                                <p className="text-gray-600">Header, footer, butonlar</p>
                            </div>
                            <div style={{ fontFamily: `"${getSettingValue('category_title_font', getSettingValue('font_family', 'Inter'))}", sans-serif` }}>
                                <p className="text-xs text-gray-500 mb-1">Kategori Başlık</p>
                                <h3 className="text-xl font-bold text-gray-900">İçecekler</h3>
                            </div>
                            <div style={{ fontFamily: `"${getSettingValue('category_description_font', getSettingValue('font_family', 'Inter'))}", sans-serif` }}>
                                <p className="text-xs text-gray-500 mb-1">Kategori Açıklama</p>
                                <p className="text-sm text-gray-600">Soğuk ve sıcak içecekler</p>
                            </div>
                            <div style={{ fontFamily: `"${getSettingValue('product_title_font', getSettingValue('font_family', 'Inter'))}", sans-serif` }}>
                                <p className="text-xs text-gray-500 mb-1">Ürün Başlık</p>
                                <h4 className="font-semibold text-gray-900">Türk Kahvesi</h4>
                            </div>
                            <div style={{ fontFamily: `"${getSettingValue('product_description_font', getSettingValue('font_family', 'Inter'))}", sans-serif` }}>
                                <p className="text-xs text-gray-500 mb-1">Ürün Açıklama</p>
                                <p className="text-sm text-gray-600">Geleneksel Türk kahvesi, orta şekerli</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navbar Items */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <span className="w-8 h-8 bg-cyan-100 rounded-lg flex items-center justify-center text-cyan-600">📱</span>
                        Alt Menü (Navbar) Öğeleri
                    </h3>
                    <button
                        onClick={() => setShowAddItem(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition text-sm font-medium"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Yeni Ekle
                    </button>
                </div>
                <p className="text-sm text-gray-500 mb-4">Sürükleyerek sıralayabilir, düzenleyebilir veya silebilirsiniz.</p>
                
                <div className="space-y-3">
                    {navbarItems.map((item, index) => (
                        <div key={item.id || index} className="flex items-center gap-3 bg-gray-50 rounded-xl p-4 group">
                            {/* Drag Handle & Enable */}
                            <div className="flex items-center gap-2">
                                <button 
                                    onClick={() => moveNavbarItem(index, -1)}
                                    disabled={index === 0}
                                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                </button>
                                <button 
                                    onClick={() => moveNavbarItem(index, 1)}
                                    disabled={index === navbarItems.length - 1}
                                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                    <input
                                        type="checkbox"
                                    checked={item.enabled !== false}
                                    onChange={(e) => onNavbarChange(index, 'enabled', e.target.checked)}
                                    className="w-5 h-5 text-orange-500 rounded focus:ring-orange-500"
                                />
                            </div>
                            
                            {/* Item Content */}
                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-3">
                                <input
                                    type="text"
                                    value={item.label}
                                    onChange={(e) => onNavbarChange(index, 'label', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    placeholder="Menü adı"
                                />
                                <IconPicker
                                    value={item.icon}
                                    onChange={(val) => onNavbarChange(index, 'icon', val)}
                                />
                                <select
                                    value={item.type || 'internal'}
                                    onChange={(e) => onNavbarChange(index, 'type', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                >
                                    {linkTypes.map(lt => (
                                        <option key={lt.value} value={lt.value}>{lt.label}</option>
                                    ))}
                                </select>
                                {/* Target/URL based on type */}
                                {(item.type === 'internal' || !item.type) && (
                                    <select
                                        value={item.target || 'home'}
                                        onChange={(e) => onNavbarChange(index, 'target', e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    >
                                        {internalTargets.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                )}
                                {item.type === 'category' && (
                                    <select
                                        value={item.target || ''}
                                        onChange={(e) => onNavbarChange(index, 'target', e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    >
                                        <option value="">Kategori Seç</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                )}
                                {(item.type === 'external' || item.type === 'whatsapp' || item.type === 'phone') && (
                                    <input
                                        type="text"
                                        value={item.url || ''}
                                        onChange={(e) => onNavbarChange(index, 'url', e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                        placeholder={item.type === 'whatsapp' ? '+90...' : item.type === 'phone' ? 'Telefon no' : 'https://...'}
                                    />
                                )}
                        </div>
                            
                            {/* Delete */}
                            <button
                                onClick={() => removeNavbarItem(index)}
                                className="p-2 text-gray-400 hover:text-red-500 transition opacity-0 group-hover:opacity-100"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        </div>
                    ))}
                    </div>

                {/* Add New Item Modal */}
                {showAddItem && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black/50" onClick={() => setShowAddItem(false)} />
                        <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
                            <h4 className="text-lg font-semibold mb-4">Yeni Menü Öğesi Ekle</h4>
                        <div className="space-y-4">
                            <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Menü Adı</label>
                                    <input
                                        type="text"
                                        value={newItem.label}
                                        onChange={(e) => setNewItem({ ...newItem, label: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        placeholder="Örn: Tatlılar"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">İkon</label>
                                    <IconPicker
                                        value={newItem.icon}
                                        onChange={(val) => setNewItem({ ...newItem, icon: val })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Link Tipi</label>
                                    <select
                                        value={newItem.type}
                                        onChange={(e) => setNewItem({ ...newItem, type: e.target.value, target: '', url: '' })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                    >
                                        {linkTypes.map(lt => (
                                            <option key={lt.value} value={lt.value}>{lt.label} - {lt.description}</option>
                                        ))}
                                    </select>
                                </div>
                                {newItem.type === 'internal' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Hedef Bölüm</label>
                                        <select
                                            value={newItem.target}
                                            onChange={(e) => setNewItem({ ...newItem, target: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        >
                                            <option value="">Seç</option>
                                            {internalTargets.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                {newItem.type === 'category' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                                        <select
                                            value={newItem.target}
                                            onChange={(e) => setNewItem({ ...newItem, target: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        >
                                            <option value="">Kategori Seç</option>
                                            {categories.map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                {(newItem.type === 'external' || newItem.type === 'whatsapp' || newItem.type === 'phone') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {newItem.type === 'whatsapp' ? 'WhatsApp Numarası' : newItem.type === 'phone' ? 'Telefon Numarası' : 'URL'}
                                </label>
                                <input
                                            type="text"
                                            value={newItem.url}
                                            onChange={(e) => setNewItem({ ...newItem, url: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                            placeholder={newItem.type === 'whatsapp' ? '+905321234567' : newItem.type === 'phone' ? '05321234567' : 'https://example.com'}
                                />
                            </div>
                                )}
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => setShowAddItem(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={addNavbarItem}
                                    disabled={!newItem.label}
                                    className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                                >
                                    Ekle
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Features Settings Component
const FeaturesSettings = ({ settings, onChange, getBooleanValue }) => {
    const featureGroups = [
        {
            title: 'Görünürlük Ayarları',
            icon: '👁️',
            color: 'blue',
            features: [
                { key: 'show_prices', label: 'Fiyatları Göster', description: 'Ürün fiyatlarını müşterilere göster' },
                { key: 'show_likes', label: 'Beğeni Sayısını Göster', description: 'Ürün beğeni sayılarını göster' },
                { key: 'show_descriptions', label: 'Açıklamaları Göster', description: 'Ürün açıklamalarını göster' },
                { key: 'show_allergens', label: 'Alerjen Bilgilerini Göster', description: 'Alerjen uyarılarını göster' },
                { key: 'show_preparation_time', label: 'Hazırlık Süresini Göster', description: 'Tahmini hazırlık süresini göster' },
                { key: 'show_calories', label: 'Kalori Bilgisini Göster', description: 'Ürün kalori değerlerini göster' }
            ]
        },
        {
            title: 'Özellik Ayarları',
            icon: '⚙️',
            color: 'green',
            features: [
                { key: 'enable_search', label: 'Arama Özelliği', description: 'Menüde arama yapılabilsin' },
                { key: 'enable_whatsapp', label: 'WhatsApp Butonu', description: 'WhatsApp ile iletişim butonu' },
                { key: 'enable_contact_form', label: 'İletişim Formu', description: 'Müşteri mesaj formu' },
                { key: 'enable_voting', label: 'Oylama Sistemi', description: 'Müşteri oylamaları' }
            ]
        }
    ];

    const handleToggle = (key) => {
        const currentValue = getBooleanValue(key);
        const newValue = (!currentValue).toString();
        onChange(key, newValue);
    };

    const getIconBgClass = (color) => {
        switch (color) {
            case 'blue': return 'bg-blue-100 text-blue-600';
            case 'green': return 'bg-green-100 text-green-600';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    return (
        <div className="space-y-8">
            {featureGroups.map(group => (
                <div key={group.title}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <span className={`w-8 h-8 ${getIconBgClass(group.color)} rounded-lg flex items-center justify-center`}>
                            {group.icon}
                        </span>
                        {group.title}
                    </h3>
                    <div className="bg-gray-50 rounded-xl divide-y divide-gray-200">
                        {group.features.map(feature => {
                            const isEnabled = getBooleanValue(feature.key);
                            return (
                                <div key={feature.key} className="flex items-center justify-between p-4 hover:bg-gray-100 transition-colors">
                                    <div className="flex-1">
                                        <p className="font-medium text-gray-900">{feature.label}</p>
                                        <p className="text-sm text-gray-500">{feature.description}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleToggle(feature.key)}
                                        className={`relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                                            isEnabled ? 'bg-orange-500' : 'bg-gray-300'
                                        }`}
                                        role="switch"
                                        aria-checked={isEnabled}
                                    >
                                        <span
                                            className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                isEnabled ? 'translate-x-6' : 'translate-x-0'
                                            }`}
                                        />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
};

// Analytics Settings Component
const AnalyticsSettings = ({ settings, onChange, getSettingValue }) => {
    return (
        <div className="space-y-8">
            {/* Google Analytics */}
                            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center text-yellow-600">📊</span>
                    Google Analytics
                </h3>
                <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Google Analytics ID
                        </label>
                                    <input
                            type="text"
                            value={getSettingValue('google_analytics_id')}
                            onChange={(e) => onChange('google_analytics_id', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="G-XXXXXXXXXX veya UA-XXXXXXXX-X"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Google Analytics Script Kodu
                                </label>
                        <textarea
                            value={getSettingValue('google_analytics_code')}
                            onChange={(e) => onChange('google_analytics_code', e.target.value)}
                            rows="6"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm resize-none"
                            placeholder="<!-- Google Analytics script kodunuzu buraya yapıştırın -->"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                            💡 Google Analytics script kodunu buraya ekleyebilirsiniz. Kod otomatik olarak menü sayfasına eklenir.
                        </p>
                            </div>
                </div>
            </div>

            {/* Hotjar / Heat Map */}
                            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center text-red-600">🔥</span>
                    Isı Haritası (Hotjar)
                </h3>
                <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Hotjar Site ID
                        </label>
                                    <input
                            type="text"
                            value={getSettingValue('hotjar_id')}
                            onChange={(e) => onChange('hotjar_id', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="1234567"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Hotjar Script Kodu
                                </label>
                        <textarea
                            value={getSettingValue('hotjar_code')}
                            onChange={(e) => onChange('hotjar_code', e.target.value)}
                            rows="6"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm resize-none"
                            placeholder="<!-- Hotjar script kodunuzu buraya yapıştırın -->"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                            💡 Hotjar, Microsoft Clarity veya başka bir ısı haritası servisinin script kodunu ekleyebilirsiniz.
                        </p>
                            </div>
                        </div>
                    </div>
                </div>
    );
};

// Custom Code Settings Component
const CustomCodeSettings = ({ settings, onChange, getSettingValue }) => {
    return (
        <div className="space-y-8">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <span className="text-xl">⚠️</span>
                <div>
                    <p className="font-medium text-amber-800">Dikkat!</p>
                    <p className="text-sm text-amber-700">
                        Bu alana eklenen kodlar doğrudan menü sayfasına eklenir. Hatalı kod sitenizin çalışmamasına neden olabilir.
                        Sadece güvendiğiniz kaynaklardan kod ekleyin.
                    </p>
            </div>
            </div>

            {/* Head Code */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600">📝</span>
                    Head Kodu
                </h3>
                <div className="bg-gray-50 rounded-xl p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        &lt;head&gt; Etiketi İçine Eklenecek Kod
                    </label>
                    <textarea
                        value={getSettingValue('custom_head_code')}
                        onChange={(e) => onChange('custom_head_code', e.target.value)}
                        rows="8"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm resize-none"
                        placeholder="<!-- Meta etiketleri, style, script vb. -->"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                        Meta etiketleri, harici CSS dosyaları veya head içinde çalışması gereken scriptler için kullanın.
                    </p>
                </div>
            </div>

            {/* Body Start Code */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">🔝</span>
                    Body Başlangıç Kodu
                </h3>
                <div className="bg-gray-50 rounded-xl p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        &lt;body&gt; Etiketinin Hemen Sonrasına Eklenecek Kod
                    </label>
                    <textarea
                        value={getSettingValue('custom_body_start_code')}
                        onChange={(e) => onChange('custom_body_start_code', e.target.value)}
                        rows="6"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm resize-none"
                        placeholder="<!-- Body başlangıcına eklenecek kodlar -->"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                        Google Tag Manager noscript kodu gibi body başlangıcında olması gereken kodlar için kullanın.
                    </p>
                </div>
            </div>

            {/* Body End Code */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center text-green-600">🔚</span>
                    Body Bitiş Kodu
                </h3>
                <div className="bg-gray-50 rounded-xl p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        &lt;/body&gt; Etiketinden Hemen Önce Eklenecek Kod
                    </label>
                    <textarea
                        value={getSettingValue('custom_body_end_code')}
                        onChange={(e) => onChange('custom_body_end_code', e.target.value)}
                        rows="6"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm resize-none"
                        placeholder="<!-- Body sonuna eklenecek kodlar -->"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                        Sayfa yüklendikten sonra çalışması gereken scriptler için kullanın.
                    </p>
                </div>
            </div>

            {/* Custom CSS */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center text-pink-600">🎨</span>
                    Özel CSS
                </h3>
                <div className="bg-gray-50 rounded-xl p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Özel CSS Stilleri
                    </label>
                    <textarea
                        value={getSettingValue('custom_css')}
                        onChange={(e) => onChange('custom_css', e.target.value)}
                        rows="10"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm resize-none"
                        placeholder={`.my-class {\n  color: red;\n  font-size: 16px;\n}`}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                        Menü görünümünü özelleştirmek için CSS kodları ekleyin. style etiketi olmadan yazın.
                    </p>
                </div>
            </div>
        </div>
    );
};

// Media Settings Component (Logo and Default Product Image)
const MediaSettings = ({ settings, organizationInfo, onChange, getSettingValue, onLogoUpload, organizationId, showToast }) => {
    const [logoUploading, setLogoUploading] = useState(false);
    const [defaultImageUploading, setDefaultImageUploading] = useState(false);
    const logoInputRef = useRef(null);
    const defaultImageInputRef = useRef(null);

    const handleLogoFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoUploading(true);
            onLogoUpload(file).finally(() => {
                setLogoUploading(false);
                if (logoInputRef.current) {
                    logoInputRef.current.value = '';
                }
            });
        }
    };

    const handleDefaultImageSelect = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setDefaultImageUploading(true);
            try {
                const formData = new FormData();
                formData.append('image', file);

                const response = await fetch(`/api/organizations/${organizationId}/settings/default-product-image`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: formData
                });

                const result = await response.json();

                if (result.success) {
                    onChange('default_product_image', result.data.image_url);
                    showToast('Varsayılan ürün görseli başarıyla yüklendi!', 'success');
                } else {
                    showToast('Görsel yüklenirken bir hata oluştu', 'error');
                }
            } catch (err) {
                showToast('Görsel yüklenirken bir hata oluştu', 'error');
            } finally {
                setDefaultImageUploading(false);
                if (defaultImageInputRef.current) {
                    defaultImageInputRef.current.value = '';
                }
            }
        }
    };

    const currentLogo = organizationInfo?.logo || getSettingValue('restaurant_logo');

    return (
        <div className="space-y-8">
            {/* Logo */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center text-green-600">🖼️</span>
                    Logo
                </h3>
                <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <p className="text-sm text-blue-800">
                            <strong>Önemli:</strong> Logo kare görsel olmalıdır. Önerilen ölçü: <strong>500px x 500px</strong>
                        </p>
                    </div>
                    {/* Current Logo Preview */}
                    {currentLogo && (
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <img 
                                    src={currentLogo.startsWith('http') ? currentLogo : currentLogo}
                                    alt="Mevcut Logo" 
                                    className="w-32 h-32 object-contain border-2 border-gray-200 rounded-xl bg-white p-2"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                                <div className="hidden w-32 h-32 border-2 border-gray-200 rounded-xl bg-gray-100 items-center justify-center">
                                    <span className="text-gray-400 text-sm">Logo yüklenemedi</span>
                                </div>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-700">Mevcut Logo</p>
                                <p className="text-xs text-gray-500">Yeni logo yüklemek için aşağıdaki butonu kullanın</p>
                            </div>
                        </div>
                    )}

                    {/* Upload Button */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Logo Yükle
                        </label>
                        <div className="flex items-center gap-4">
                            <input
                                ref={logoInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleLogoFileSelect}
                                className="hidden"
                                id="logo-upload"
                            />
                            <label
                                htmlFor="logo-upload"
                                className={`flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-semibold hover:from-orange-600 hover:to-red-600 transition cursor-pointer ${
                                    logoUploading ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                                {logoUploading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                        </svg>
                                        Yükleniyor...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                        </svg>
                                        Logo Yükle
                                    </>
                                )}
                            </label>
                            <p className="text-sm text-gray-500">
                                PNG, JPG, GIF (Max 5MB) - Kare görsel önerilir (500x500px)
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Default Product Image */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">📦</span>
                    Varsayılan Ürün Görseli
                </h3>
                <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                        Görseli olmayan ürünler için kullanılacak varsayılan görsel. Bu görsel tüm görseli olmayan ürünlerde gösterilecektir.
                    </p>
                    {/* Current Default Product Image Preview */}
                    {getSettingValue('default_product_image') && (
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <img 
                                    src={getImageUrl(getSettingValue('default_product_image'))}
                                    alt="Varsayılan Ürün Görseli" 
                                    className="w-32 h-32 object-cover border-2 border-gray-200 rounded-xl bg-white"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                                <div className="hidden w-32 h-32 border-2 border-gray-200 rounded-xl bg-gray-100 items-center justify-center">
                                    <span className="text-gray-400 text-sm">Görsel yüklenemedi</span>
                                </div>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-700">Mevcut Varsayılan Görsel</p>
                                <button
                                    onClick={() => onChange('default_product_image', '')}
                                    className="mt-2 text-sm text-red-600 hover:text-red-700"
                                >
                                    Görseli Kaldır
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Upload Button */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Varsayılan Ürün Görseli Yükle
                        </label>
                        <div className="flex items-center gap-4">
                            <input
                                ref={defaultImageInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleDefaultImageSelect}
                                className="hidden"
                                id="default-image-upload"
                            />
                            <label
                                htmlFor="default-image-upload"
                                className={`flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-semibold hover:from-orange-600 hover:to-red-600 transition cursor-pointer ${
                                    defaultImageUploading ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                                {defaultImageUploading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                        </svg>
                                        Yükleniyor...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                        </svg>
                                        Görsel Yükle
                                    </>
                                )}
                            </label>
                            <p className="text-sm text-gray-500">
                                PNG, JPG, GIF (Max 5MB)
                            </p>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            Bu görsel görseli olmayan tüm ürünlerde gösterilecektir.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
